import { useState } from "react";
import { Download, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { downloadSh1Pdf, downloadSh4Pdf } from "@/lib/api";
import { isoToDmy, dmyToIso } from "@/lib/date";
import MemberPicker from "@/components/MemberPicker";

function FormField({ k, l, t, testidPrefix, value, onChange }) {
  if (t === "date") {
    return (
      <label>
        <span className="text-[10px] uppercase tracking-wider text-slate-500">{l}</span>
        <input
          value={isoToDmy(value)}
          onChange={(e) => onChange(k, e.target.value)}
          onBlur={(e) => {
            const iso = dmyToIso(e.target.value);
            if (iso || !e.target.value) onChange(k, iso);
          }}
          placeholder="DD-MM-YYYY"
          inputMode="numeric"
          className="w-full border border-slate-300 px-3 py-2 mt-1 focus:outline-none focus:border-blue-700"
          data-testid={`${testidPrefix}-${k}`}
        />
      </label>
    );
  }
  return (
    <label>
      <span className="text-[10px] uppercase tracking-wider text-slate-500">{l}</span>
      <input
        type={t === "number" ? "number" : "text"}
        value={value}
        onChange={(e) => onChange(k, e.target.value)}
        className="w-full border border-slate-300 px-3 py-2 mt-1 focus:outline-none focus:border-blue-700"
        data-testid={`${testidPrefix}-${k}`}
      />
    </label>
  );
}

function ModalShell({ title, onClose, children, maxWidth, testid }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className={`bg-white ${maxWidth || "max-w-2xl"} w-full max-h-[90vh] overflow-y-auto p-6`}
        onClick={(e) => e.stopPropagation()}
        data-testid={testid}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function GenerateButton({ busy, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      disabled={busy}
      className="px-4 py-2 bg-blue-700 text-white text-sm flex items-center gap-2 disabled:opacity-50"
      data-testid={testid}
    >
      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
      Generate PDF
    </button>
  );
}

const SH1_FIELDS = [
  ["certificate_no", "Certificate No."],
  ["folio_no", "Folio No."],
  ["member_name", "Member Name"],
  ["share_class", "Class of Shares"],
  ["no_of_shares", "No. of Shares", "number"],
  ["face_value", "Face Value (INR)", "number"],
  ["paid_up_per_share", "Paid-up / share (INR)", "number"],
  ["distinctive_from", "Distinctive No. From"],
  ["distinctive_to", "Distinctive No. To"],
  ["date_of_issue", "Date of Issue", "date"],
];

export function Sh1Modal({ company, onClose }) {
  const [f, setF] = useState({
    certificate_no: "",
    folio_no: "",
    member_name: "",
    address: "",
    no_of_shares: 100,
    share_class: "Equity",
    face_value: 10,
    paid_up_per_share: 10,
    distinctive_from: "",
    distinctive_to: "",
    date_of_issue: new Date().toISOString().slice(0, 10),
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF({ ...f, [k]: v });
  const submit = async () => {
    setBusy(true);
    try {
      await downloadSh1Pdf(company.id, f);
      toast.success("SH-1 PDF downloaded");
      onClose();
    } catch {
      toast.error("Could not generate certificate");
    } finally { setBusy(false); }
  };
  return (
    <ModalShell title="Form SH-1 · Share Certificate" onClose={onClose} testid="sh1-modal">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
        {SH1_FIELDS.map(([k, l, t]) => (
          <FormField key={k} k={k} l={l} t={t} testidPrefix="sh1" value={f[k]} onChange={set} />
        ))}
        <label className="md:col-span-2">
          <span className="text-[10px] uppercase tracking-wider text-slate-500">Address</span>
          <textarea
            value={f.address}
            onChange={(e) => set("address", e.target.value)}
            rows={2}
            className="w-full border border-slate-300 px-3 py-2 mt-1"
            data-testid="sh1-address"
          />
        </label>
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <button onClick={onClose} className="px-4 py-2 border border-slate-300 text-sm">Cancel</button>
        <GenerateButton busy={busy} onClick={submit} testid="sh1-generate" />
      </div>
    </ModalShell>
  );
}

const SH4_FIELDS = [
  ["instrument_no", "Instrument No."],
  ["date_of_execution", "Date of Execution", "date"],
  ["transferor_name", "Transferor Name"],
  ["transferor_folio", "Transferor Folio"],
  ["transferee_name", "Transferee Name"],
  ["transferee_folio", "Transferee Folio (if existing)"],
  ["transferee_father", "Transferee Father's/Husband's Name"],
  ["transferee_occupation", "Transferee Occupation"],
  ["no_of_shares", "No. of Shares", "number"],
  ["share_class", "Class of Shares"],
  ["distinctive_from", "Distinctive No. From"],
  ["distinctive_to", "Distinctive No. To"],
  ["consideration", "Consideration (INR)"],
  ["certificate_no", "Certificate No."],
  ["stamp_duty_paid", "Stamp Duty Paid (INR)"],
];

export function Sh4Modal({ company, onClose }) {
  const [f, setF] = useState({
    instrument_no: "",
    date_of_execution: new Date().toISOString().slice(0, 10),
    transferor_name: "",
    transferor_folio: "",
    transferor_address: "",
    transferee_name: "",
    transferee_folio: "",
    transferee_address: "",
    transferee_father: "",
    transferee_occupation: "",
    no_of_shares: 0,
    share_class: "Equity Shares",
    distinctive_from: "",
    distinctive_to: "",
    consideration: "",
    certificate_no: "",
    stamp_duty_paid: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF({ ...f, [k]: v });
  const setMany = (patch) => setF((prev) => ({ ...prev, ...patch }));
  const members = company?.members || [];

  // SH-4 is a stand-alone PDF generator. It does NOT auto-update MGT-1 or
  // SH-6 — users should pass an explicit share-transfer board resolution
  // (which IS auto-linked) when they want the registers to reflect the
  // transfer. Keeping these two flows separate avoids accidental double-entry
  // when the SH-4 is generated for compliance evidence ahead of board
  // approval.
  const submit = async () => {
    setBusy(true);
    try {
      await downloadSh4Pdf(company.id, f);
      toast.success("SH-4 PDF downloaded");
      onClose();
    } catch {
      toast.error("Could not generate transfer deed");
    } finally { setBusy(false); }
  };

  const SH4_INLINE_FIELDS = SH4_FIELDS.filter(
    ([k]) => k !== "transferor_name" && k !== "transferee_name",
  );

  return (
    <ModalShell title="Form SH-4 · Share Transfer Deed" onClose={onClose} maxWidth="max-w-3xl" testid="sh4-modal">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
        <label>
          <span className="text-[10px] uppercase tracking-wider text-slate-500">Transferor Name</span>
          <div className="mt-1">
            <MemberPicker
              members={members}
              value={f.transferor_name}
              onChange={(v) => set("transferor_name", v)}
              onPickMember={(m) => {
                if (m) {
                  setMany({
                    transferor_name: m.name || "",
                    transferor_folio: m.folio_no || "",
                    transferor_address: m.address || f.transferor_address,
                  });
                }
              }}
              testId="sh4-transferor_name"
              placeholder="Pick from members or type a name"
            />
          </div>
        </label>
        <label>
          <span className="text-[10px] uppercase tracking-wider text-slate-500">Transferee Name</span>
          <div className="mt-1">
            <MemberPicker
              members={members}
              value={f.transferee_name}
              onChange={(v) => set("transferee_name", v)}
              onPickMember={(m) => {
                if (m) {
                  setMany({
                    transferee_name: m.name || "",
                    transferee_folio: m.folio_no || "",
                    transferee_address: m.address || f.transferee_address,
                    transferee_father: m.father_name || f.transferee_father,
                    transferee_occupation: m.occupation || f.transferee_occupation,
                  });
                }
              }}
              testId="sh4-transferee_name"
              placeholder="Pick from members or type a new shareholder"
            />
          </div>
        </label>
        {SH4_INLINE_FIELDS.map(([k, l, t]) => (
          <FormField key={k} k={k} l={l} t={t} testidPrefix="sh4" value={f[k]} onChange={set} />
        ))}
        <label className="md:col-span-2">
          <span className="text-[10px] uppercase tracking-wider text-slate-500">Transferor Address</span>
          <textarea
            value={f.transferor_address}
            onChange={(e) => set("transferor_address", e.target.value)}
            rows={2}
            className="w-full border border-slate-300 px-3 py-2 mt-1"
          />
        </label>
        <label className="md:col-span-2">
          <span className="text-[10px] uppercase tracking-wider text-slate-500">Transferee Address</span>
          <textarea
            value={f.transferee_address}
            onChange={(e) => set("transferee_address", e.target.value)}
            rows={2}
            className="w-full border border-slate-300 px-3 py-2 mt-1"
          />
        </label>
      </div>
      <p className="mt-4 text-[11px] text-slate-500 italic">
        Note: SH-4 generates the transfer deed PDF only. To update MGT-1 (members register) and SH-6
        (register of share transfer), pass a "Share Transfer" board resolution from the Generate page —
        it will auto-link the share movement to both registers.
      </p>
      <div className="mt-5 flex justify-end gap-2">
        <button onClick={onClose} className="px-4 py-2 border border-slate-300 text-sm">Cancel</button>
        <GenerateButton busy={busy} onClick={submit} testid="sh4-generate" />
      </div>
    </ModalShell>
  );
}
